import * as i0 from '@angular/core';
import { Component, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@fgb/core';
import { MarketplaceItemStatus } from '@fgb/core';
import * as i3 from '@fgb/portal-component-library/src/lib/shared/sort-bar';
import { SortOrder, SortBarModule } from '@fgb/portal-component-library/src/lib/shared/sort-bar';
import moment from 'moment/moment';
import * as i4 from '@fgb/portal-component-library/src/lib/shared/loading';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import * as i5 from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list-item';
import { MarketplaceListItemModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list-item';
import * as i6 from '@fgb/portal-component-library/src/lib/directives';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import * as i7 from '@ngx-translate/core';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';

var MarketplaceListSortOption;
(function (MarketplaceListSortOption) {
    MarketplaceListSortOption[MarketplaceListSortOption["Ordinal"] = 0] = "Ordinal";
    MarketplaceListSortOption[MarketplaceListSortOption["Points"] = 1] = "Points";
})(MarketplaceListSortOption || (MarketplaceListSortOption = {}));

class MarketplaceListComponent {
    constructor(marketplaceExtendedQuery, marketplaceService) {
        this.marketplaceExtendedQuery = marketplaceExtendedQuery;
        this.marketplaceService = marketplaceService;
        this.showSortBar = true;
        this.defaultSort = MarketplaceListSortOption.Points;
        this.sortOrderType = SortOrder;
    }
    ngOnInit() {
        this.marketplaceService.fetchMarketplaceItems().toPromise();
        this.queryMarketplaceItems();
        this.subscribeAndSortMarketplaceItems();
        this.defineSortBarOptions();
    }
    ngOnDestroy() {
        if (this.marketplaceItemsSubscription) {
            this.marketplaceItemsSubscription.unsubscribe();
        }
    }
    queryMarketplaceItems() {
        if (this.marketplaceItemTypes) {
            this.marketplaceItems$ = this.marketplaceExtendedQuery.selectExtendedMarketplaceItemsOfType(...this.marketplaceItemTypes);
        }
        else {
            this.marketplaceItems$ = this.marketplaceExtendedQuery.selectExtendedMarketplaceItems();
        }
    }
    subscribeAndSortMarketplaceItems() {
        this.marketplaceItemsSubscription = this.marketplaceItems$.subscribe((items) => {
            if (items) {
                items.filter((item) => {
                    const today = moment().utc();
                    const endDate = moment.utc(item.ItemEndDate);
                    return !item.ComingSoon || (item.ComingSoon && endDate.isAfter(today));
                });
                items = this._sortItems(items);
                this.filteredMarketplaceItems = items;
            }
        });
    }
    defineSortBarOptions() {
        this.sortBarOptions = [
            {
                propertyToSort: 'PointValue',
                shouldToggle: true,
                order: this.sortOrderType.Descending,
                labelASC: 'fgbcl.points.low.to.high',
                labelDESC: 'fgbcl.points.high.to.low',
            },
            {
                propertyToSort: 'ItemEndDate',
                order: this.sortOrderType.Ascending,
                shouldToggle: false,
                labelASC: 'fgbcl.sortby.ending.soon',
            },
            {
                propertyToSort: 'AvailableCount',
                order: this.sortOrderType.Descending,
                shouldToggle: true,
                labelASC: 'fgbcl.limited.availability',
                labelDESC: 'fgbcl.high.availability',
            },
            {
                propertyToSort: 'DisplayName',
                order: this.sortOrderType.Descending,
                shouldToggle: true,
                labelASC: 'fgbcl.sortby.name.atoz',
                labelDESC: 'fgbcl.sortby.name.ztoa',
            },
        ];
    }
    resetOrder() {
        this.subscribeAndSortMarketplaceItems();
    }
    _sortItems(items) {
        let sortedItemsMap = new Map();
        // split out for all marketplace status
        for (let status in MarketplaceItemStatus) {
            if (isNaN(Number(status))) {
                sortedItemsMap.set(status, items
                    .filter((x) => x.Status === MarketplaceItemStatus[status])
                    .sort((a, b) => {
                    switch (this.defaultSort) {
                        case MarketplaceListSortOption.Points:
                            return this.sortByPoints(a, b);
                        case MarketplaceListSortOption.Ordinal:
                            return this.sortByOrdinal(a, b);
                        default:
                            return 0;
                    }
                }));
            }
        }
        let sortedItems = [];
        let availableItems = sortedItemsMap.get(MarketplaceItemStatus[MarketplaceItemStatus.Available]);
        if (availableItems) {
            sortedItems.push(...availableItems);
        }
        let notEnoughPointsItems = sortedItemsMap.get(MarketplaceItemStatus[MarketplaceItemStatus.NotEnoughPoints]);
        if (notEnoughPointsItems) {
            sortedItems.push(...notEnoughPointsItems);
        }
        let limitReachedItems = sortedItemsMap.get(MarketplaceItemStatus[MarketplaceItemStatus.MemberLimitReached]);
        if (limitReachedItems) {
            sortedItems.push(...limitReachedItems);
        }
        let soldOutItems = sortedItemsMap.get(MarketplaceItemStatus[MarketplaceItemStatus.SoldOut]);
        if (soldOutItems) {
            sortedItems.push(...soldOutItems);
        }
        let comingSoonItems = sortedItemsMap.get(MarketplaceItemStatus[MarketplaceItemStatus.ComingSoon]);
        if (comingSoonItems) {
            sortedItems.push(...comingSoonItems);
        }
        sortedItems = this.sortItemsFavouriteAndNewest(sortedItems);
        return sortedItems;
    }
    sortItemsFavouriteAndNewest(marketplaceItemExtended) {
        const threeDays = 3;
        const itemsNew = marketplaceItemExtended.filter((items, index) => {
            const startDate = moment().diff(items?.ItemStartDate, 'days');
            if (items.Active && startDate <= threeDays) {
                marketplaceItemExtended.splice(index, 1);
                return items;
            }
            else
                return;
        });
        const itemsFavourite = marketplaceItemExtended.filter((items, index) => {
            if (items.Favourite)
                marketplaceItemExtended.splice(index, 1);
            return items.Favourite;
        });
        return itemsNew.concat(itemsFavourite).concat(marketplaceItemExtended);
    }
    sortByPoints(a, b) {
        return b.PointValue - a.PointValue;
    }
    sortByOrdinal(a, b) {
        return a.Ordinal - b.Ordinal;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: MarketplaceListComponent, deps: [{ token: i1.MarketplaceExtendedQuery }, { token: i1.MarketplaceService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.2.1", type: MarketplaceListComponent, selector: "fgbcl-marketplace-list", inputs: { take: "take", isList: "isList", showSortBar: "showSortBar", marketplaceItemTypes: "marketplaceItemTypes", marketplaceItemView: "marketplaceItemView", eventItemView: "eventItemView", productsItemView: "productsItemView", defaultSort: "defaultSort" }, ngImport: i0, template: "<div [class.row]=\"isList\">\r\n  <ng-container *ngIf=\"marketplaceItems$ | async as marketplaceItems; else loadingRef\">\r\n    <ng-container *ngIf=\"filteredMarketplaceItems.length; else noMarketplaceItems\">\r\n      <ng-container *fgbWebsiteContentFeatureFlag=\"'fgbcl.feature.flag.marketplace.sortbar'\">\r\n        <fgbcl-sort-bar\r\n          *ngIf=\"showSortBar\"\r\n          class=\"row w-100\"\r\n          [sortBarOptions]=\"sortBarOptions\"\r\n          [itemList]=\"filteredMarketplaceItems\"\r\n          [displayDefaultSort]=\"true\"\r\n          (defaultSortAction)=\"resetOrder()\"\r\n        ></fgbcl-sort-bar>\r\n      </ng-container>\r\n      <div *ngFor=\"let item of filteredMarketplaceItems\" [class.col-lg-4]=\"isList\" [class.col-6]=\"isList\">\r\n        <div class=\"loyalty-item-container\">\r\n          <fgbcl-marketplace-list-item [marketplaceItem]=\"item\"></fgbcl-marketplace-list-item>\r\n        </div>\r\n      </div>\r\n    </ng-container>\r\n  </ng-container>\r\n</div>\r\n\r\n<!-- No results -->\r\n<ng-template #noMarketplaceItems>\r\n  <!--Marketplace Placeholder-->\r\n  <div class=\"no-items w-100 mt-4 no-results-placeholder-div\" *ngIf=\"marketplaceItemView\">\r\n    <div class=\"no-results-placeholder\">{{ 'marketplace.placeholder.description' | translate }}</div>\r\n  </div>\r\n  <!--Events Placeholder-->\r\n  <div class=\"no-items w-100 mt-4 no-results-placeholder-div\" *ngIf=\"eventItemView\">\r\n    <div class=\"no-results-placeholder\">{{ 'events.placeholder.description' | translate }}</div>\r\n  </div>\r\n  <!--Product Placeholder-->\r\n  <div class=\"no-items w-100 mt-4 no-results-placeholder-div\" *ngIf=\"productsItemView\">\r\n    <div class=\"no-results-placeholder\">{{ 'products.placeholder.description' | translate }}</div>\r\n  </div>\r\n</ng-template>\r\n\r\n<!-- Loading -->\r\n<ng-template #loadingRef>\r\n  <div class=\"w-100\">\r\n    <fgbcl-loading></fgbcl-loading>\r\n  </div>\r\n</ng-template>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3.SortBarComponent, selector: "fgbcl-sort-bar", inputs: ["sortBarOptions", "itemList", "displayDefaultSort"], outputs: ["defaultSortAction"] }, { kind: "component", type: i4.LoadingComponent, selector: "fgbcl-loading", inputs: ["size"] }, { kind: "component", type: i5.MarketplaceListItemComponent, selector: "fgbcl-marketplace-list-item", inputs: ["marketplaceItem", "ignoreLink"] }, { kind: "directive", type: i6.WebsiteContentFeatureFlagDirective, selector: "[fgbWebsiteContentFeatureFlag]", inputs: ["fgbWebsiteContentFeatureFlag"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "pipe", type: i7.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: MarketplaceListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fgbcl-marketplace-list', template: "<div [class.row]=\"isList\">\r\n  <ng-container *ngIf=\"marketplaceItems$ | async as marketplaceItems; else loadingRef\">\r\n    <ng-container *ngIf=\"filteredMarketplaceItems.length; else noMarketplaceItems\">\r\n      <ng-container *fgbWebsiteContentFeatureFlag=\"'fgbcl.feature.flag.marketplace.sortbar'\">\r\n        <fgbcl-sort-bar\r\n          *ngIf=\"showSortBar\"\r\n          class=\"row w-100\"\r\n          [sortBarOptions]=\"sortBarOptions\"\r\n          [itemList]=\"filteredMarketplaceItems\"\r\n          [displayDefaultSort]=\"true\"\r\n          (defaultSortAction)=\"resetOrder()\"\r\n        ></fgbcl-sort-bar>\r\n      </ng-container>\r\n      <div *ngFor=\"let item of filteredMarketplaceItems\" [class.col-lg-4]=\"isList\" [class.col-6]=\"isList\">\r\n        <div class=\"loyalty-item-container\">\r\n          <fgbcl-marketplace-list-item [marketplaceItem]=\"item\"></fgbcl-marketplace-list-item>\r\n        </div>\r\n      </div>\r\n    </ng-container>\r\n  </ng-container>\r\n</div>\r\n\r\n<!-- No results -->\r\n<ng-template #noMarketplaceItems>\r\n  <!--Marketplace Placeholder-->\r\n  <div class=\"no-items w-100 mt-4 no-results-placeholder-div\" *ngIf=\"marketplaceItemView\">\r\n    <div class=\"no-results-placeholder\">{{ 'marketplace.placeholder.description' | translate }}</div>\r\n  </div>\r\n  <!--Events Placeholder-->\r\n  <div class=\"no-items w-100 mt-4 no-results-placeholder-div\" *ngIf=\"eventItemView\">\r\n    <div class=\"no-results-placeholder\">{{ 'events.placeholder.description' | translate }}</div>\r\n  </div>\r\n  <!--Product Placeholder-->\r\n  <div class=\"no-items w-100 mt-4 no-results-placeholder-div\" *ngIf=\"productsItemView\">\r\n    <div class=\"no-results-placeholder\">{{ 'products.placeholder.description' | translate }}</div>\r\n  </div>\r\n</ng-template>\r\n\r\n<!-- Loading -->\r\n<ng-template #loadingRef>\r\n  <div class=\"w-100\">\r\n    <fgbcl-loading></fgbcl-loading>\r\n  </div>\r\n</ng-template>\r\n" }]
        }], ctorParameters: () => [{ type: i1.MarketplaceExtendedQuery }, { type: i1.MarketplaceService }], propDecorators: { take: [{
                type: Input
            }], isList: [{
                type: Input
            }], showSortBar: [{
                type: Input
            }], marketplaceItemTypes: [{
                type: Input
            }], marketplaceItemView: [{
                type: Input
            }], eventItemView: [{
                type: Input
            }], productsItemView: [{
                type: Input
            }], defaultSort: [{
                type: Input
            }] } });

class MarketplaceListModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: MarketplaceListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.2.1", ngImport: i0, type: MarketplaceListModule, declarations: [MarketplaceListComponent], imports: [CommonModule,
            SortBarModule,
            LoadingModule,
            MarketplaceListItemModule,
            DirectivesModule,
            FGBSharedModule], exports: [MarketplaceListComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: MarketplaceListModule, imports: [CommonModule,
            SortBarModule,
            LoadingModule,
            MarketplaceListItemModule,
            DirectivesModule,
            FGBSharedModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: MarketplaceListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MarketplaceListComponent],
                    imports: [
                        CommonModule,
                        SortBarModule,
                        LoadingModule,
                        MarketplaceListItemModule,
                        DirectivesModule,
                        FGBSharedModule
                    ],
                    exports: [MarketplaceListComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MarketplaceListComponent, MarketplaceListModule, MarketplaceListSortOption };
